export type InvoicePlanSaveErrorCode =
  | 'due_invalid'
  | 'end_invalid'
  | 'financial_invalid'
  | 'interval_invalid'
  | 'location_invalid'
  | 'location_missing'
  | 'location_stale'
  | 'no_future_period'
  | 'notification_invalid'
  | 'pending_transition'
  | 'plan_not_active'
  | 'plan_not_found'
  | 'recovery_not_paused'
  | 'recovery_stale'
  | 'send_elapsed'
  | 'service_boundary_invalid'
  | 'service_direction_invalid'
  | 'service_mode_invalid'
  | 'stale';

export class InvoicePlanSaveError extends Error {
  readonly code: InvoicePlanSaveErrorCode;

  constructor(code: InvoicePlanSaveErrorCode) {
    super(INTERNAL_ERROR_MESSAGES[code]);
    this.name = 'InvoicePlanSaveError';
    this.code = code;
  }
}

const INTERNAL_ERROR_MESSAGES: Record<InvoicePlanSaveErrorCode, string> = {
  due_invalid: 'The retained due date is invalid or changed.',
  end_invalid: 'The recurring plan end date is invalid.',
  financial_invalid: 'The recurring financial amounts are invalid or do not match the submitted total.',
  interval_invalid: 'Choose a different supported billing interval.',
  location_invalid: 'The plan location timezone is invalid.',
  location_missing: 'The plan location is missing. Reload Billing Plans.',
  location_stale: 'The plan location changed while you were editing. Reload it before saving again.',
  no_future_period: 'This change has no future full-period invoice before the plan end date.',
  notification_invalid: 'Notification days must be a non-negative whole number.',
  pending_transition: 'This recurring plan already has a pending interval transition.',
  plan_not_active: 'Only an active recurring plan can change billing interval.',
  plan_not_found: 'This recurring plan no longer exists. Reload Billing Plans.',
  recovery_not_paused: 'Only a paused cadence transition can be recovered.',
  recovery_stale: 'This recovery attempt is stale. Reload Billing Plans.',
  send_elapsed: 'The current send date has passed. Reload Billing Plans.',
  service_boundary_invalid: 'The last generated service boundary is invalid.',
  service_direction_invalid: 'Billing interval changes only support the upcoming service period.',
  service_mode_invalid: 'Choose whether service dates are enabled before saving.',
  stale: 'This recurring plan changed while you were editing. Reload it before saving again.',
};

export function getInvoicePlanSaveErrorKey(error: unknown) {
  if (!(error instanceof InvoicePlanSaveError)) return 'billing.invoicePlan.saveFailed';
  return `billing.invoicePlan.errors.${error.code}`;
}
